import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import AppleAuthTransition from "../../../../components/ui/auth/AppleAuthTransition";
import GoogleAuthTransition from "../../../../components/ui/auth/GoogleAuthTransition";
import { getAppleAuthConfig } from "../../../../utils/auth/appleAuthConfig";
import { authorizeWithApple } from "../../../../utils/auth/authorizeWithApple";
import { consumeAppleRedirectResult } from "../../../../utils/auth/consumeAppleRedirectResult";
import {
  completeAppleLogin,
  completeGoogleLogin,
  finalizeSocialLoginSession,
} from "../../../../utils/auth/socialAuthHelpers";
import { getReferralCookie } from "../../../../utils/cookies/cookieHelpers";
import {
  getNativeBridge,
  getSocialLoginCapabilities,
  isSocialLoginCapabilitiesSupported,
  isSocialLoginBridgeAvailable,
} from "../../../../utils/native/nativeBridge";
import { redeemSocialLoginViaNativeBridge } from "../../../../utils/native/mobileHandoffRedemption";

function GoogleAuthButton({
  redirectPath,
  onError,
  mobileTransaction = false,
  isNativeSocialLoginPending,
  beginNativeSocialLogin,
  endNativeSocialLogin,
}) {
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);
  const exchangedCodeRef = useRef(null);
  const isMountedRef = useRef(true);
  // Only true after Google returns the code; never while the popup is open.
  const [isCompletingLogin, setIsCompletingLogin] = useState(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const resetGoogleLogin = () => {
    isSubmittingRef.current = false;
    exchangedCodeRef.current = null;
    setIsCompletingLogin(false);
  };

  const finishGoogleLogin = async (code) => {
    // An authorization code can be exchanged only once.
    if (exchangedCodeRef.current === code) {
      return;
    }
    exchangedCodeRef.current = code;

    try {
      const result = await completeGoogleLogin({
        code,
        // Implicit consent is shown on both sign-in and sign-up pages.
        termsAccepted: true,
        mobileTransaction,
      });

      if (!result.ok) {
        resetGoogleLogin();
        onError?.(result.message);
        return;
      }

      // Navigate while the transition is visible; finally always clears it.
      navigate(redirectPath || "/", { replace: true });
    } catch (error) {
      resetGoogleLogin();
      onError?.("Network error. Please check your connection.");
    }
  };

  const loginWithGoogle = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    onSuccess: ({ code }) => {
      if (!code) {
        resetGoogleLogin();
        onError?.("Google did not return a valid authorization code.");
        return;
      }

      // Popup already closed — only now change Plaintr UI.
      setIsCompletingLogin(true);
      void finishGoogleLogin(code);
    },
    onError: () => {
      resetGoogleLogin();
      onError?.("Google sign-in was cancelled or failed.");
    },
    onNonOAuthError: () => {
      resetGoogleLogin();
    },
  });

  const finishNativeSocialLogin = async () => {
    // The handoff code is single-use and already consumed once redemption
    // succeeds, so finalizeSocialLoginSession must always run below —
    // isMountedRef only gates this component's own state and navigation.
    let result;
    try {
      result = await redeemSocialLoginViaNativeBridge("google", {
        termsAccepted: true,
        referralCode: getReferralCookie() || null,
      });
    } catch (error) {
      endNativeSocialLogin();
      if (isMountedRef.current) {
        resetGoogleLogin();
        onError?.("Network error. Please check your connection.");
      }
      return;
    }

    if (!result.ok) {
      endNativeSocialLogin();
      if (isMountedRef.current) {
        resetGoogleLogin();
        onError?.(result.message);
      }
      return;
    }

    try {
      await finalizeSocialLoginSession(result.data);
    } finally {
      endNativeSocialLogin();
    }

    if (isMountedRef.current) {
      navigate(redirectPath || "/", { replace: true });
    }
  };

  const handleGoogleClick = () => {
    if (isSubmittingRef.current || isCompletingLogin) {
      return;
    }

    if (isSocialLoginBridgeAvailable()) {
      // Native supports only one pending social-login request.
      if (isNativeSocialLoginPending) {
        return;
      }

      isSubmittingRef.current = true;
      beginNativeSocialLogin();
      setIsCompletingLogin(true);
      void finishNativeSocialLogin();
      return;
    }

    isSubmittingRef.current = true;

    // No loading state here — the browser stays unchanged behind the popup.
    try {
      loginWithGoogle();
    } catch (error) {
      resetGoogleLogin();
      onError?.("Unable to open Google sign-in.");
    }
  };

  return (
    <Fragment>
      <button
        type="button"
        className="social-auth-item social-auth-item--google"
        onClick={handleGoogleClick}
        disabled={isCompletingLogin || isNativeSocialLoginPending}
        aria-label="Continue with Google"
        aria-busy={isCompletingLogin}
      >
        <img
          src={`${process.env.PUBLIC_URL}/images/google_logo.svg`}
          alt="Google Logo"
        />
      </button>

      {isCompletingLogin && <GoogleAuthTransition />}
    </Fragment>
  );
}

function AppleAuthButton({
  redirectPath,
  onError,
  mobileTransaction = false,
  isNativeSocialLoginPending,
  beginNativeSocialLogin,
  endNativeSocialLogin,
}) {
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);
  const exchangedCodeRef = useRef(null);
  const isMountedRef = useRef(true);
  const redirectHandledRef = useRef(false);
  // Only true after Apple returns the code; never while the popup is open.
  const [isCompletingLogin, setIsCompletingLogin] = useState(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const resetAppleLogin = () => {
    isSubmittingRef.current = false;
    exchangedCodeRef.current = null;
    if (isMountedRef.current) {
      setIsCompletingLogin(false);
    }
  };

  const finishAppleLogin = async (authorization) => {
    // An authorization code can be exchanged only once.
    if (exchangedCodeRef.current === authorization.code) {
      resetAppleLogin();
      return;
    }
    exchangedCodeRef.current = authorization.code;

    try {
      const result = await completeAppleLogin({
        code: authorization.code,
        state: authorization.state,
        redirectUri: authorization.redirectUri,
        // Implicit consent is shown on both sign-in and sign-up pages.
        termsAccepted: true,
        firstName: authorization.firstName,
        lastName: authorization.lastName,
        mobileTransaction,
      });

      if (!result.ok) {
        if (isMountedRef.current) {
          onError?.(result.message);
        }
        return;
      }

      // Navigate while the transition is visible; finally always clears it.
      if (isMountedRef.current) {
        navigate(redirectPath || "/", { replace: true });
      }
    } catch (error) {
      if (isMountedRef.current) {
        onError?.("Network error. Please check your connection.");
      }
    } finally {
      resetAppleLogin();
    }
  };

  useEffect(() => {
    if (redirectHandledRef.current) {
      return;
    }

    const redirectResult = consumeAppleRedirectResult();
    if (!redirectResult) {
      return;
    }

    redirectHandledRef.current = true;

    if (!redirectResult.ok) {
      if (!redirectResult.cancelled) {
        onError?.(redirectResult.message);
      }
      return;
    }

    isSubmittingRef.current = true;
    setIsCompletingLogin(true);
    void finishAppleLogin(redirectResult);
    // finishAppleLogin is stable enough for this mount-only return path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  const finishNativeSocialLogin = async () => {
    let result;
    try {
      result = await redeemSocialLoginViaNativeBridge("apple");
    } catch (error) {
      endNativeSocialLogin();
      resetAppleLogin();
      if (isMountedRef.current) {
        onError?.("Network error. Please check your connection.");
      }
      return;
    }

    if (!result.ok) {
      endNativeSocialLogin();
      resetAppleLogin();
      if (isMountedRef.current) {
        onError?.(result.message);
      }
      return;
    }

    try {
      await finalizeSocialLoginSession(result.data);
    } finally {
      endNativeSocialLogin();
      resetAppleLogin();
    }

    if (isMountedRef.current) {
      navigate(redirectPath || "/", { replace: true });
    }
  };

  const handleAppleClick = async () => {
    if (isSubmittingRef.current || isCompletingLogin) {
      return;
    }

    if (isSocialLoginBridgeAvailable()) {
      // Native supports only one pending social-login request.
      if (isNativeSocialLoginPending) {
        return;
      }

      isSubmittingRef.current = true;
      beginNativeSocialLogin();
      setIsCompletingLogin(true);
      void finishNativeSocialLogin();
      return;
    }

    isSubmittingRef.current = true;

    // No loading state here — the browser stays unchanged behind the popup.
    try {
      const authorization = await authorizeWithApple();

      if (authorization.redirected) {
        // Full-page redirect to Apple; return via callback query params.
        return;
      }

      if (!authorization.ok) {
        resetAppleLogin();
        if (authorization.cancelled) {
          return;
        }
        onError?.(authorization.message);
        return;
      }

      // Popup already closed — only now change Plaintr UI.
      setIsCompletingLogin(true);
      void finishAppleLogin(authorization);
    } catch (error) {
      resetAppleLogin();
      onError?.("Unable to open Apple sign-in.");
    }
  };

  return (
    <Fragment>
      <button
        type="button"
        className="social-auth-item social-auth-item--apple"
        onClick={handleAppleClick}
        disabled={isCompletingLogin || isNativeSocialLoginPending}
        aria-label="Continue with Apple"
        aria-busy={isCompletingLogin}
      >
        <img
          src={`${process.env.PUBLIC_URL}/images/apple_logo.svg`}
          alt="Apple Logo"
        />
      </button>

      {isCompletingLogin && <AppleAuthTransition />}
    </Fragment>
  );
}

function DisabledSocialAuthButton({
  provider,
  logoSrc,
  logoAlt,
  ariaLabel,
  onError,
  message,
}) {
  return (
    <button
      type="button"
      className={`social-auth-item social-auth-item--${provider}`}
      onClick={() => {
        onError?.(message);
      }}
      aria-label={ariaLabel}
    >
      <img src={logoSrc} alt={logoAlt} />
    </button>
  );
}

function SocialAuth({
  mode = "sign-in",
  redirectPath = "/",
  onError,
  mobileTransaction = false,
}) {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isGoogleEnabled = Boolean(googleClientId);
  const appleConfig = getAppleAuthConfig();
  const isAppleEnabled = appleConfig.isConfigured;

  const isNativeContext = !!getNativeBridge();
  // A legacy wrapper (bridge present, capabilities method absent) keeps the
  // pre-capabilities behavior below, same as normal web. A wrapper that
  // implements the method but returns invalid data fails closed instead.
  const capabilitiesSupported =
    isNativeContext && isSocialLoginCapabilitiesSupported();
  const capabilities = capabilitiesSupported
    ? getSocialLoginCapabilities()
    : null;
  const showGoogle =
    isGoogleEnabled &&
    (!capabilitiesSupported || !!capabilities?.providers.includes("google"));
  const showApple =
    isAppleEnabled &&
    (!capabilitiesSupported || !!capabilities?.providers.includes("apple"));

  // Shared lock: native supports only one pending social-login request,
  // regardless of which provider started it.
  const [isNativeSocialLoginPending, setIsNativeSocialLoginPending] =
    useState(false);
  const beginNativeSocialLogin = () => setIsNativeSocialLoginPending(true);
  const endNativeSocialLogin = () => setIsNativeSocialLoginPending(false);

  const content = (
    <Fragment>
      <div className="social-auth-container">
        {showGoogle ? (
          <GoogleAuthButton
            redirectPath={redirectPath}
            onError={onError}
            mobileTransaction={mobileTransaction}
            isNativeSocialLoginPending={isNativeSocialLoginPending}
            beginNativeSocialLogin={beginNativeSocialLogin}
            endNativeSocialLogin={endNativeSocialLogin}
          />
        ) : (
          !isGoogleEnabled && (
            <DisabledSocialAuthButton
              provider="google"
              logoSrc={`${process.env.PUBLIC_URL}/images/google_logo.svg`}
              logoAlt="Google Logo"
              ariaLabel="Continue with Google"
              onError={onError}
              message="Google sign-in is not configured for this environment."
            />
          )
        )}

        {showApple ? (
          <AppleAuthButton
            redirectPath={redirectPath}
            onError={onError}
            mobileTransaction={mobileTransaction}
            isNativeSocialLoginPending={isNativeSocialLoginPending}
            beginNativeSocialLogin={beginNativeSocialLogin}
            endNativeSocialLogin={endNativeSocialLogin}
          />
        ) : (
          !isAppleEnabled && (
            <DisabledSocialAuthButton
              provider="apple"
              logoSrc={`${process.env.PUBLIC_URL}/images/apple_logo.svg`}
              logoAlt="Apple Logo"
              ariaLabel="Continue with Apple"
              onError={onError}
              message="Apple sign-in is not configured for this environment."
            />
          )
        )}
      </div>
    </Fragment>
  );

  if (!isGoogleEnabled) {
    return content;
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      {content}
    </GoogleOAuthProvider>
  );
}

export default SocialAuth;
